import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import {
  selectedOutlineSx,
  labelStyle,
  inputStyle,
  addButtonStyle,
  removeButtonStyle,
  itemCardStyle,
} from "../../config/settingsStyles";
import { TextField, TextareaField, ColorField, SliderField, SettingsSection, SettingsRow, SelectField, CheckboxField } from "../ui/form/SettingsField";
import { ImageUpload } from "../ui/ImageUpload";

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

export interface FooterProps {
  clinicName: string;
  logoUrl?: string;
  secondLogoUrl?: string;
  description?: string;
  columns: FooterColumn[];
  phone?: string;
  email?: string;
  address?: string;
  copyright?: string;
  columnAlign?: "left" | "right";
  showClinicName?: boolean;
  showContactIcons?: boolean;
  legalLinks?: FooterLink[];
  backgroundColor?: string;
  textColor?: string;
  minHeight?: number;
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    line?: string;
    youtube?: string;
    tiktok?: string;
    x?: string;
    linkedin?: string;
  };
}

// Image paths for social platforms with PNG icons
const footerSocialIconImages: Record<string, string> = {
  facebook: "/static/images/social/facebook.png",
  instagram: "/static/images/social/ig.png",
  line: "/static/images/social/line.png",
  youtube: "/static/images/social/youtube.png",
  tiktok: "/static/images/social/tiktok.png",
  x: "/static/images/social/twitter.png",
  linkedin: "/static/images/social/linked.png",
};

// Footer Social Icon component - uses PNG images
const FooterSocialIcon = ({ platform, size = 24 }: { platform: string; size?: number }) => {
  if (footerSocialIconImages[platform]) {
    return (
      <img
        src={footerSocialIconImages[platform]}
        alt={platform}
        style={{ width: size, height: size, objectFit: "contain" }}
      />
    );
  }
  return null;
};

export const Footer = ({
  clinicName = "Premium Clinic",
  logoUrl,
  secondLogoUrl,
  description,
  columns = [],
  phone,
  email,
  address,
  copyright,
  columnAlign = "left",
  showClinicName = true,
  showContactIcons = true,
  legalLinks = [],
  backgroundColor = "#1f2937",
  textColor = "#ffffff",
  minHeight,
  maxWidth = 1280,
  paddingX,
  paddingTop,
  paddingBottom,
  socialLinks,
}: FooterProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { inEditor } = useEditor((state) => ({
    inEditor: true,
  }));

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (inEditor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const currentYear = new Date().getFullYear();

  // Column alignment is a prop set from the settings panel: "right" pushes the
  // link columns to the right edge (brand left / links right, empty middle).
  const alignRight = columnAlign === "right";

  return (
    <Box
      component="footer"
      ref={(ref: HTMLDivElement | null) => {
        (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        if (ref) connect(drag(ref));
      }}
      sx={{
        pt: paddingTop != null ? `${paddingTop}px` : (isMobile ? "32px" : "48px"),
        pb: paddingBottom != null ? `${paddingBottom}px` : (isMobile ? "32px" : "48px"),
        px: paddingX != null ? `${paddingX}px` : (isMobile ? "16px" : "24px"),
        bgcolor: backgroundColor,
        color: textColor,
        minHeight: minHeight ? `${minHeight}px` : undefined,
        ...(selected && selectedOutlineSx),
      }}
    >
      <Box sx={{ maxWidth: maxWidth ? `${maxWidth}px` : "1280px", mx: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : (alignRight ? `1fr repeat(${columns.length}, auto)` : `1fr repeat(${columns.length}, 1fr)`),
            gap: isMobile ? "24px" : "32px",
            mb: isMobile ? "24px" : "32px",
          }}
        >
          <Box sx={{ gridColumn: "1" }}>
            {(logoUrl || secondLogoUrl) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt={clinicName}
                    style={{ height: 40, width: "auto" }}
                  />
                )}
                {secondLogoUrl && (
                  <img
                    src={secondLogoUrl}
                    alt=""
                    style={{ height: 40, width: "auto" }}
                  />
                )}
              </Box>
            )}
            {showClinicName && (
              <Box sx={{ mb: isMobile ? "12px" : "16px" }}>
                <Element
                  id="footer-name"
                  is={Text}
                  text={clinicName}
                  fontSize={20}
                  fontWeight="bold"
                  tagName="h3"
                  color={textColor}
                />
              </Box>
            )}
            {description && (
              <Box sx={{ opacity: 0.8, mb: isMobile ? "12px" : "16px" }}>
                <Element
                  id="footer-desc"
                  is={Text}
                  text={description}
                  fontSize={14}
                  tagName="p"
                  color={textColor}
                />
              </Box>
            )}
            {legalLinks.filter((l) => l.label && l.href).length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: isMobile ? "12px" : "16px" }}>
                {legalLinks.filter((l) => l.label && l.href).map((link, i, arr) => (
                  <React.Fragment key={i}>
                    <Box
                      component="a"
                      href={link.href}
                      onClick={handleLinkClick}
                      sx={{ color: textColor, opacity: 0.8, textDecoration: "none", fontSize: "0.875rem", "&:hover": { textDecoration: "underline" } }}
                    >
                      {link.label}
                    </Box>
                    {i < arr.length - 1 && <Box sx={{ opacity: 0.4 }}>|</Box>}
                  </React.Fragment>
                ))}
              </Box>
            )}
            {address && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1, opacity: 0.8 }}>
                {showContactIcons && <Typography sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>📍</Typography>}
                <Element id="footer-address" is={Text} text={address} fontSize={14} fontWeight="normal" tagName="span" color={textColor} />
              </Box>
            )}
            {phone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, opacity: 0.8 }}>
                {showContactIcons && <Typography sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>📞</Typography>}
                <Element id="footer-phone" is={Text} text={phone} fontSize={14} fontWeight="normal" tagName="span" color={textColor} />
              </Box>
            )}
            {email && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.8 }}>
                {showContactIcons && <Typography sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}>✉️</Typography>}
                <Element id="footer-email" is={Text} text={email} fontSize={14} fontWeight="normal" tagName="span" color={textColor} />
              </Box>
            )}

            {socialLinks && (
              <Box
                sx={{
                  display: "flex",
                  gap: isMobile ? "12px" : "16px",
                  mt: isMobile ? "12px" : "16px",
                }}
              >
                {socialLinks.facebook && (
                  <Box
                    component="a"
                    href={socialLinks.facebook}
                    onClick={handleLinkClick}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <FooterSocialIcon platform="facebook" size={isMobile ? 20 : 24} />
                  </Box>
                )}
                {socialLinks.instagram && (
                  <Box
                    component="a"
                    href={socialLinks.instagram}
                    onClick={handleLinkClick}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <FooterSocialIcon platform="instagram" size={isMobile ? 20 : 24} />
                  </Box>
                )}
                {socialLinks.line && (
                  <Box
                    component="a"
                    href={socialLinks.line}
                    onClick={handleLinkClick}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <FooterSocialIcon platform="line" size={isMobile ? 20 : 24} />
                  </Box>
                )}
                {socialLinks.youtube && (
                  <Box
                    component="a"
                    href={socialLinks.youtube}
                    onClick={handleLinkClick}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <FooterSocialIcon platform="youtube" size={isMobile ? 20 : 24} />
                  </Box>
                )}
                {socialLinks.tiktok && (
                  <Box
                    component="a"
                    href={socialLinks.tiktok}
                    onClick={handleLinkClick}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <FooterSocialIcon platform="tiktok" size={isMobile ? 20 : 24} />
                  </Box>
                )}
                {socialLinks.x && (
                  <Box
                    component="a"
                    href={socialLinks.x}
                    onClick={handleLinkClick}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <FooterSocialIcon platform="x" size={isMobile ? 20 : 24} />
                  </Box>
                )}
                {socialLinks.linkedin && (
                  <Box
                    component="a"
                    href={socialLinks.linkedin}
                    onClick={handleLinkClick}
                    sx={{
                      color: "inherit",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 0.7 },
                    }}
                  >
                    <FooterSocialIcon platform="linkedin" size={isMobile ? 20 : 24} />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {columns.map((column, index) => {
            const validLinks = column.links.filter((link) => link.label && link.href);
            if (validLinks.length === 0) return null;
            return (
            <Box key={index} sx={alignRight ? { display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" } : undefined}>
              {column.title && (
                <Element
                  id={`footer-col-title-${index}`}
                  is={Text}
                  text={column.title}
                  fontSize={16}
                  fontWeight="semibold"
                  tagName="h4"
                  color={textColor}
                  marginBottom={isMobile ? 12 : 16}
                />
              )}
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  "& li": { mb: isMobile ? "6px" : "8px" },
                }}
              >
                {validLinks.map((link, linkIndex) => (
                  <li key={linkIndex} style={alignRight ? { textAlign: "right" } : undefined}>
                    <Box
                      component="a"
                      href={link.href}
                      onClick={handleLinkClick}
                      sx={{
                        opacity: 0.8,
                        color: "inherit",
                        textDecoration: "none",
                        display: alignRight ? "inline-flex" : "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexDirection: alignRight ? "row-reverse" : "row",
                        "&:hover": { opacity: 1, textDecoration: "underline" },
                      }}
                    >
                      {link.href.includes("facebook") && <FooterSocialIcon platform="facebook" size={16} />}
                      {(link.href.includes("line.me") || link.href.includes("lin.ee")) && <FooterSocialIcon platform="line" size={16} />}
                      <Element
                        id={`footer-link-${index}-${linkIndex}`}
                        is={Text}
                        text={link.label}
                        fontSize={14}
                        fontWeight="normal"
                        tagName="span"
                        color={textColor}
                      />
                    </Box>
                  </li>
                ))}
              </Box>
            </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "rgba(156, 163, 175, 0.4)",
            pt: isMobile ? "24px" : "32px",
            textAlign: "center",
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            opacity: 0.7,
          }}
        >
          <Element
            id="footer-copyright"
            is={Text}
            text={copyright || `© ${currentYear} ${clinicName}. All rights reserved.`}
            fontSize={14}
            fontWeight="normal"
            tagName="span"
            color={textColor}
            textAlign="center"
          />
        </Box>
      </Box>
    </Box>
  );
};

export const FooterSettings = () => {
  const {
    actions: { setProp },
    logoUrl,
    secondLogoUrl,
    columns,
    phone,
    email,
    address,
    copyright,
    columnAlign,
    showClinicName,
    showContactIcons,
    legalLinks,
    backgroundColor,
    textColor,
    minHeight,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
    socialLinks,
  } = useNode((node) => ({
    logoUrl: node.data.props.logoUrl,
    secondLogoUrl: node.data.props.secondLogoUrl,
    columns: node.data.props.columns,
    phone: node.data.props.phone,
    email: node.data.props.email,
    address: node.data.props.address,
    copyright: node.data.props.copyright,
    columnAlign: node.data.props.columnAlign,
    showClinicName: node.data.props.showClinicName,
    showContactIcons: node.data.props.showContactIcons,
    legalLinks: node.data.props.legalLinks,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    minHeight: node.data.props.minHeight,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
    socialLinks: node.data.props.socialLinks,
  }));

  const updateColumn = (colIndex: number, field: string, value: string) => {
    setProp((props: FooterProps) => {
      const newColumns = [...props.columns];
      newColumns[colIndex] = { ...newColumns[colIndex], [field]: value };
      props.columns = newColumns;
    });
  };

  const updateColumnLink = (colIndex: number, linkIndex: number, field: keyof FooterLink, value: string) => {
    setProp((props: FooterProps) => {
      const newColumns = [...props.columns];
      const newLinks = [...newColumns[colIndex].links];
      newLinks[linkIndex] = { ...newLinks[linkIndex], [field]: value };
      newColumns[colIndex] = { ...newColumns[colIndex], links: newLinks };
      props.columns = newColumns;
    });
  };

  const addColumnLink = (colIndex: number) => {
    setProp((props: FooterProps) => {
      const newColumns = [...props.columns];
      newColumns[colIndex] = {
        ...newColumns[colIndex],
        links: [...newColumns[colIndex].links, { label: "New Link", href: "/" }],
      };
      props.columns = newColumns;
    });
  };

  const removeColumnLink = (colIndex: number, linkIndex: number) => {
    setProp((props: FooterProps) => {
      const newColumns = [...props.columns];
      newColumns[colIndex] = {
        ...newColumns[colIndex],
        links: newColumns[colIndex].links.filter((_, i) => i !== linkIndex),
      };
      props.columns = newColumns;
    });
  };

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <ImageUpload
        value={logoUrl || ""}
        onChange={(url) => setProp((props: FooterProps) => (props.logoUrl = url || undefined))}
        label="Logo Image (optional)"
      />

      <ImageUpload
        value={secondLogoUrl || ""}
        onChange={(url) => setProp((props: FooterProps) => (props.secondLogoUrl = url || undefined))}
        label="Second Logo (optional)"
      />

      <TextField
        label="Address"
        value={address || ""}
        onChange={(v) => setProp((props: FooterProps) => (props.address = v || undefined))}
      />

      <SettingsRow>
        <TextField
          label="Phone"
          value={phone || ""}
          onChange={(v) => setProp((props: FooterProps) => (props.phone = v || undefined))}
        />
        <TextField
          label="Email"
          value={email || ""}
          onChange={(v) => setProp((props: FooterProps) => (props.email = v || undefined))}
        />
      </SettingsRow>

      <div>
        <label style={labelStyle}>Social Links</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <input
            type="text"
            value={socialLinks?.facebook || ""}
            onChange={(e) =>
              setProp((props: FooterProps) => {
                props.socialLinks = { ...props.socialLinks, facebook: e.target.value || undefined };
              })
            }
            style={inputStyle}
            placeholder="Facebook URL"
          />
          <input
            type="text"
            value={socialLinks?.instagram || ""}
            onChange={(e) =>
              setProp((props: FooterProps) => {
                props.socialLinks = { ...props.socialLinks, instagram: e.target.value || undefined };
              })
            }
            style={inputStyle}
            placeholder="Instagram URL"
          />
          <input
            type="text"
            value={socialLinks?.line || ""}
            onChange={(e) =>
              setProp((props: FooterProps) => {
                props.socialLinks = { ...props.socialLinks, line: e.target.value || undefined };
              })
            }
            style={inputStyle}
            placeholder="Line URL"
          />
          <input
            type="text"
            value={socialLinks?.youtube || ""}
            onChange={(e) =>
              setProp((props: FooterProps) => {
                props.socialLinks = { ...props.socialLinks, youtube: e.target.value || undefined };
              })
            }
            style={inputStyle}
            placeholder="Youtube URL"
          />
          <input
            type="text"
            value={socialLinks?.tiktok || ""}
            onChange={(e) =>
              setProp((props: FooterProps) => {
                props.socialLinks = { ...props.socialLinks, tiktok: e.target.value || undefined };
              })
            }
            style={inputStyle}
            placeholder="TikTok URL"
          />
          <input
            type="text"
            value={socialLinks?.x || ""}
            onChange={(e) =>
              setProp((props: FooterProps) => {
                props.socialLinks = { ...props.socialLinks, x: e.target.value || undefined };
              })
            }
            style={inputStyle}
            placeholder="X (Twitter) URL"
          />
          <input
            type="text"
            value={socialLinks?.linkedin || ""}
            onChange={(e) =>
              setProp((props: FooterProps) => {
                props.socialLinks = { ...props.socialLinks, linkedin: e.target.value || undefined };
              })
            }
            style={inputStyle}
            placeholder="LinkedIn URL"
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>Footer Columns</label>
        {columns.map((col: FooterColumn, colIndex: number) => (
          <div key={colIndex} style={itemCardStyle}>
            {/* Column header with reorder and delete */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <div style={{ display: "flex", gap: "4px" }}>
                <button
                  onClick={() => setProp((props: FooterProps) => {
                    if (colIndex === 0) return;
                    const newCols = [...props.columns];
                    [newCols[colIndex - 1], newCols[colIndex]] = [newCols[colIndex], newCols[colIndex - 1]];
                    props.columns = newCols;
                  })}
                  disabled={colIndex === 0}
                  style={{ ...removeButtonStyle, opacity: colIndex === 0 ? 0.3 : 1, padding: "4px 8px" }}
                >
                  ↑
                </button>
                <button
                  onClick={() => setProp((props: FooterProps) => {
                    if (colIndex === props.columns.length - 1) return;
                    const newCols = [...props.columns];
                    [newCols[colIndex], newCols[colIndex + 1]] = [newCols[colIndex + 1], newCols[colIndex]];
                    props.columns = newCols;
                  })}
                  disabled={colIndex === columns.length - 1}
                  style={{ ...removeButtonStyle, opacity: colIndex === columns.length - 1 ? 0.3 : 1, padding: "4px 8px" }}
                >
                  ↓
                </button>
              </div>
              <button
                onClick={() => setProp((props: FooterProps) => {
                  props.columns = props.columns.filter((_, i) => i !== colIndex);
                })}
                style={removeButtonStyle}
              >
                ×
              </button>
            </div>
            <input
              type="text"
              value={col.title}
              onChange={(e) => updateColumn(colIndex, "title", e.target.value)}
              style={{ ...inputStyle, fontWeight: 500, marginBottom: "8px" }}
              placeholder="Column Title"
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {col.links.map((link: FooterLink, linkIndex: number) => (
                <div key={linkIndex} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px", flexShrink: 0 }}>
                    <button
                      onClick={() => setProp((props: FooterProps) => {
                        if (linkIndex === 0) return;
                        const newCols = [...props.columns];
                        const newLinks = [...newCols[colIndex].links];
                        [newLinks[linkIndex - 1], newLinks[linkIndex]] = [newLinks[linkIndex], newLinks[linkIndex - 1]];
                        newCols[colIndex] = { ...newCols[colIndex], links: newLinks };
                        props.columns = newCols;
                      })}
                      disabled={linkIndex === 0}
                      style={{ ...removeButtonStyle, opacity: linkIndex === 0 ? 0.3 : 1, padding: "2px 6px", fontSize: "0.7rem" }}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => setProp((props: FooterProps) => {
                        if (linkIndex === col.links.length - 1) return;
                        const newCols = [...props.columns];
                        const newLinks = [...newCols[colIndex].links];
                        [newLinks[linkIndex], newLinks[linkIndex + 1]] = [newLinks[linkIndex + 1], newLinks[linkIndex]];
                        newCols[colIndex] = { ...newCols[colIndex], links: newLinks };
                        props.columns = newCols;
                      })}
                      disabled={linkIndex === col.links.length - 1}
                      style={{ ...removeButtonStyle, opacity: linkIndex === col.links.length - 1 ? 0.3 : 1, padding: "2px 6px", fontSize: "0.7rem" }}
                    >
                      ↓
                    </button>
                  </div>
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => updateColumnLink(colIndex, linkIndex, "label", e.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder="Label"
                  />
                  <input
                    type="text"
                    value={link.href}
                    onChange={(e) => updateColumnLink(colIndex, linkIndex, "href", e.target.value)}
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder="/path"
                  />
                  <button onClick={() => removeColumnLink(colIndex, linkIndex)} style={removeButtonStyle}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => addColumnLink(colIndex)} style={addButtonStyle}>
              + Add Link
            </button>
          </div>
        ))}
        <button
          onClick={() => setProp((props: FooterProps) => {
            props.columns = [...props.columns, { title: "New Column", links: [] }];
          })}
          style={addButtonStyle}
        >
          + Add Column
        </button>
      </div>

      <TextField
        label="Copyright Text"
        value={copyright || ""}
        onChange={(v) => setProp((props: FooterProps) => (props.copyright = v || undefined))}
        placeholder="Leave empty for default"
      />

      <SelectField
        label="Column Alignment"
        value={columnAlign || "left"}
        onChange={(v) => setProp((props: FooterProps) => (props.columnAlign = v as "left" | "right"))}
        options={[
          { value: "left", label: "Left (spread evenly)" },
          { value: "right", label: "Right (links to far right)" },
        ]}
      />

      <CheckboxField
        id="footer-show-clinic-name"
        label="Show Title / Name"
        checked={showClinicName !== false}
        onChange={(v) => setProp((props: FooterProps) => (props.showClinicName = v))}
      />

      <CheckboxField
        id="footer-show-contact-icons"
        label="Show Contact Icons (📍 📞 ✉️)"
        checked={showContactIcons !== false}
        onChange={(v) => setProp((props: FooterProps) => (props.showContactIcons = v))}
      />

      <div>
        <label style={labelStyle}>Legal Links (Privacy / Terms)</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {(legalLinks || []).map((link: FooterLink, i: number) => (
            <div key={i} style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <input
                type="text"
                value={link.label}
                onChange={(e) => setProp((props: FooterProps) => {
                  const arr = [...(props.legalLinks || [])];
                  arr[i] = { ...arr[i], label: e.target.value };
                  props.legalLinks = arr;
                })}
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Label"
              />
              <input
                type="text"
                value={link.href}
                onChange={(e) => setProp((props: FooterProps) => {
                  const arr = [...(props.legalLinks || [])];
                  arr[i] = { ...arr[i], href: e.target.value };
                  props.legalLinks = arr;
                })}
                style={{ ...inputStyle, flex: 1 }}
                placeholder="/path"
              />
              <button
                onClick={() => setProp((props: FooterProps) => {
                  props.legalLinks = (props.legalLinks || []).filter((_, idx) => idx !== i);
                })}
                style={removeButtonStyle}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => setProp((props: FooterProps) => {
            props.legalLinks = [...(props.legalLinks || []), { label: "New Link", href: "/" }];
          })}
          style={addButtonStyle}
        >
          + Add Legal Link
        </button>
      </div>

      <ColorField
        label="Background Color"
        value={backgroundColor}
        onChange={(v) => setProp((props: FooterProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: FooterProps) => (props.textColor = v))}
      />

      <SliderField
        label="Min Height"
        value={minHeight || 0}
        onChange={(v) => setProp((props: FooterProps) => (props.minHeight = v || undefined))}
        min={0}
        max={600}
        unit="px"
      />

      <SliderField
        label="Max Width"
        value={maxWidth || 1280}
        onChange={(v) => setProp((props: FooterProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX ?? 24}
        onChange={(v) => setProp((props: FooterProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop ?? 48}
        onChange={(v) => setProp((props: FooterProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom ?? 48}
        onChange={(v) => setProp((props: FooterProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />
    </SettingsSection>
  );
};

Footer.craft = {
  displayName: "Footer",
  isCanvas: true,
  props: {
    clinicName: "Premium Clinic",
    logoUrl: "",
    secondLogoUrl: undefined,
    description: "Providing excellent healthcare services with compassion and expertise.",
    phone: "02-123-4567",
    email: "contact@clinic.com",
    address: "123 Medical Center Road, Bangkok",
    copyright: undefined,
    columns: [
      {
        title: "Services",
        links: [
          { label: "Dermatology", href: "/services/dermatology" },
          { label: "Aesthetics", href: "/services/aesthetics" },
          { label: "Wellness", href: "/services/wellness" },
        ],
      },
      {
        title: "Quick Links",
        links: [
          { label: "About Us", href: "/about" },
          { label: "Our Doctors", href: "/doctors" },
          { label: "Contact", href: "/contact" },
        ],
      },
    ],
    columnAlign: "left",
    showClinicName: true,
    showContactIcons: true,
    legalLinks: [],
    backgroundColor: "#1f2937",
    textColor: "#ffffff",
    minHeight: undefined,
    maxWidth: 1280,
    paddingX: undefined,
    paddingTop: undefined,
    paddingBottom: undefined,
    socialLinks: {
      facebook: "https://facebook.com",
      instagram: "https://instagram.com",
      line: "https://line.me",
    },
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: FooterSettings,
  },
};
